/**
 * Фільтр «Період» (місяць + рік) для ЗП-списків — у рядку фільтрів під
 * назвою (канон Норми #29).
 *
 * Механіка (рішення власника 2026-09-10, ч.775): поля сторінки СПОЧАТКУ
 * ПОРОЖНІ і «дивляться вгору» — діє «період за замовчуванням» з шапки
 * (місяць/квартал/рік/довільний/усі). Щойно користувач заповнив місяць
 * та/або рік на сторінці — список показує ЛИШЕ обраний період сторінки.
 */
import { useMemo, useState } from 'react';
import { useDefaults } from '@/store/defaults';

export const MONTHS_UK = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

interface Props {
  month: number | '';
  year: number | '';
  onMonth: (v: number | '') => void;
  onYear: (v: number | '') => void;
}

/** Параметри періоду нарахування для list-API (точковий або діапазон YYYY-MM). */
export interface HrPeriodParams {
  period_year?: number;
  period_month?: number;
  period_from?: string;
  period_to?: string;
}

export interface HrPeriodFilterState {
  month: number | '';
  year: number | '';
  setMonth: (v: number | '') => void;
  setYear: (v: number | '') => void;
  /** Готові параметри для API: локальні поля сторінки > період шапки. */
  params: HrPeriodParams;
  /** Чи задано період саме на сторінці (override шапки). */
  isLocal: boolean;
  /** Частина deps/queryKey. */
  key: (string | number | undefined)[];
  /** Пропси для <PeriodFilter {...bind} />. */
  bind: Props;
}

const pad2 = (n: number) => String(n).padStart(2, '0');

/** Період шапки → параметри list-API ЗП-документів (період нарахування). */
export function hrPeriodParamsFromHeader(d: {
  periodMode: string; periodYear: number; periodMonth: number; periodQuarter: number;
  customFrom: string; customTo: string;
}): HrPeriodParams {
  switch (d.periodMode) {
    case 'month':
      return { period_year: d.periodYear, period_month: d.periodMonth };
    case 'quarter': {
      const q = d.periodQuarter;
      return {
        period_from: `${d.periodYear}-${pad2(q * 3 - 2)}`,
        period_to: `${d.periodYear}-${pad2(q * 3)}`,
      };
    }
    case 'year':
      return { period_year: d.periodYear };
    case 'custom':
      return {
        period_from: d.customFrom ? d.customFrom.slice(0, 7) : undefined,
        period_to: d.customTo ? d.customTo.slice(0, 7) : undefined,
      };
    default:
      return {};
  }
}

export function useHrPeriodFilter(): HrPeriodFilterState {
  const [month, setMonth] = useState<number | ''>('');
  const [year, setYear] = useState<number | ''>('');
  const { periodMode, periodYear, periodMonth, periodQuarter, customFrom, customTo } = useDefaults();
  const isLocal = month !== '' || year !== '';
  const params = useMemo<HrPeriodParams>(() => {
    if (isLocal) {
      // Лише місяць без року — місяць року з шапки; лише рік — усі місяці року.
      return { period_year: year || periodYear, period_month: month || undefined };
    }
    return hrPeriodParamsFromHeader({ periodMode, periodYear, periodMonth, periodQuarter, customFrom, customTo });
  }, [isLocal, month, year, periodMode, periodYear, periodMonth, periodQuarter, customFrom, customTo]);
  return {
    month, year, setMonth, setYear, params, isLocal,
    key: [params.period_year, params.period_month, params.period_from, params.period_to],
    bind: { month, year, onMonth: setMonth, onYear: setYear },
  };
}

export default function PeriodFilter({ month, year, onMonth, onYear }: Props) {
  const now = new Date().getFullYear();
  return (
    <div style={{ display: 'flex', gap: 4 }}
      title="Порожньо — період за замовчуванням з шапки; заповнено — лише обраний період">
      <select value={month} autoComplete="off" onChange={(e) => onMonth(e.target.value ? Number(e.target.value) : '')}
        style={{ width: 140 }}>
        <option value=""></option>
        {MONTHS_UK.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
      </select>
      <select value={year} autoComplete="off" onChange={(e) => onYear(e.target.value ? Number(e.target.value) : '')}
        style={{ width: 110 }}>
        <option value=""></option>
        {Array.from({ length: 7 }, (_, i) => now - 5 + i).map((y) => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  );
}
